import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../hooks/useAuth';
import { useGetInvite, useAcceptInvite, useGetUserLeagues } from '../api/leaguesApi';
import posthog from 'posthog-js';
import { FormResult } from '../components/common/FormResult';
import { SignupGateModal } from '../components/auth/SignupGateModal';
import { getInviteeJoinabilityMessage } from '../utils/inviteErrors';

function LeagueInvitePage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { user, isGuest: authIsGuest, loading: authLoading } = useAuth();
    const isGuest = authIsGuest === true || user?.isGuest === true;
    const [guestGateOpen, setGuestGateOpen] = useState(false);
    const { data: invite, isLoading, isError, error } = useGetInvite(token);
    const acceptInvite = useAcceptInvite();

    // Only an explicit joinable === false blocks the page: older cached
    // responses without the field must keep the normal accept flow.
    const inviteBlocked = invite?.status === 'pending' && invite?.joinable === false;
    // A blocked invite is not an error for someone who is already in the
    // league, so check membership before rendering the unavailable state.
    const { data: userLeagues, isLoading: userLeaguesLoading } = useGetUserLeagues(
        Boolean(user) && inviteBlocked
    );

    const redirectTarget = `/invite/${encodeURIComponent(token || '')}`;

    if (isLoading || authLoading || (inviteBlocked && user && userLeaguesLoading)) {
        return (
            <div className="page-login">
                <div className="wrapper">
                    <h1>Invitation</h1>
                    <div className="form-block">
                        <div className="description">Loading invitation…</div>
                    </div>
                </div>
            </div>
        );
    }

    if (isError) {
        const message = error?.response?.data?.error || 'Invitation not found.';
        return (
            <div className="page-login">
                <div className="wrapper">
                    <h1>Invitation</h1>
                    <div className="form-block">
                        <FormResult
                            show={true}
                            props={{
                                icon: 'r-b-cross',
                                color: '#FF4F4F',
                                title: 'Invitation invalid',
                                description: message
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    }

    const leagueName = invite?.league?.name || 'this league';
    const inviterName = invite?.inviter?.name || 'A friend';
    const status = invite?.status;

    if (status && status !== 'pending') {
        const titleMap = {
            accepted: 'Invitation already accepted',
            rejected: 'Invitation declined',
            expired: 'Invitation expired'
        };
        const descMap = {
            accepted: `You have already joined ${leagueName}.`,
            rejected: 'This invitation has been declined.',
            expired: 'This invitation is no longer valid. Ask the league owner to send a new one.'
        };
        return (
            <div className="page-login">
                <div className="wrapper">
                    <h1>Invitation</h1>
                    <div className="form-block">
                        <FormResult
                            show={true}
                            props={{
                                icon: status === 'accepted' ? 'icon-r-check' : 'r-b-cross',
                                color: status === 'accepted' ? '#00CD5C' : '#FF4F4F',
                                title: titleMap[status] || 'Invitation unavailable',
                                description: descMap[status] || ''
                            }}
                        />
                        {status === 'accepted' && invite?.league?.slug && (
                            <Link to={`/${invite.league.slug}/table`} className="submit-btn" style={{ marginTop: '1rem' }}>
                                GO TO LEAGUE
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (inviteBlocked) {
        const inviteLeagueId = invite?.league?.id || invite?.league?._id;
        const inviteLeagueSlug = invite?.league?.slug;
        const isAlreadyMember = Boolean(user) && (userLeagues || []).some((league) => (
            (inviteLeagueId && String(league._id) === String(inviteLeagueId))
            || (inviteLeagueSlug && league.slug === inviteLeagueSlug)
        ));

        if (isAlreadyMember) {
            return (
                <div className="page-login">
                    <div className="wrapper">
                        <h1>Invitation</h1>
                        <div className="form-block">
                            <FormResult
                                show={true}
                                props={{
                                    icon: 'icon-r-check',
                                    color: '#00CD5C',
                                    title: "You're already in this league",
                                    description: `You're already a member of ${leagueName}.`
                                }}
                            />
                            {inviteLeagueSlug && (
                                <Link to={`/${inviteLeagueSlug}/table`} className="submit-btn" style={{ marginTop: '1rem' }}>
                                    GO TO LEAGUE
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="page-login">
                <div className="wrapper">
                    <h1>Invitation</h1>
                    <div className="form-block">
                        <FormResult
                            show={true}
                            props={{
                                icon: 'r-b-cross',
                                color: '#FF4F4F',
                                title: 'Invitation unavailable',
                                description: getInviteeJoinabilityMessage(invite?.joinabilityReason)
                            }}
                        />
                        {/* A logged-out existing member still needs a path in. */}
                        {!user && (
                            <Link
                                to={`/login?redirect=${encodeURIComponent(redirectTarget)}`}
                                className="submit-btn"
                                style={{ marginTop: '1rem' }}
                            >
                                LOG IN
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (!user) {
        const loginUrl = `/login?redirect=${encodeURIComponent(redirectTarget)}`;
        const registerUrl = `/register?redirect=${encodeURIComponent(redirectTarget)}`;
        return (
            <div className="page-login">
                <div className="wrapper">
                    <h1>You're invited!</h1>
                    <div className="form-block">
                        <div className="description">
                            <strong>{inviterName}</strong> invited you to join <strong>{leagueName}</strong> on Nova.
                            Log in or create an account to accept.
                        </div>
                        <Link to={loginUrl} className="submit-btn" style={{ marginTop: '1rem' }}>
                            LOG IN
                        </Link>
                        <Link to={registerUrl} className="submit-btn" style={{ marginTop: '0.75rem' }}>
                            REGISTER
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const handleAccept = async () => {
        try {
            const result = await acceptInvite.mutateAsync(token);
            const displayName = result.league?.name || leagueName;
            const alreadyMember = result.message === 'Already a member';
            if (!alreadyMember) {
                posthog.capture('league_invite_accepted', { league_id: result.league?.id, league_name: displayName });
                posthog.capture('invite_link_joined', {
                    league_id: result.leagueId || result.league?.id || result.league?._id,
                    team_id: result.teamId
                });
            }
            toast.success(alreadyMember
                ? `You're already a member of ${displayName}.`
                : `You joined ${displayName}!`);
            const slug = result.leagueSlug || result.league?.slug || invite?.league?.slug;
            const goesToTeamPitch = Boolean(slug && result.teamId);
            const destination = goesToTeamPitch
                ? `/${slug}/team-pitch/${result.teamId}`
                : (slug ? `/${slug}/table` : '/');
            // The team pitch resolves league gameMode and ownership from
            // requests that land after its own data, so pass what the accept
            // response already knows and the squad reveal can show on its
            // first render instead of flashing the normal page.
            navigate(destination, {
                replace: true,
                state: goesToTeamPitch && result.league?.gameMode
                    ? { squadReveal: { teamId: result.teamId, gameMode: result.league.gameMode } }
                    : undefined
            });
        } catch (err) {
            if (err?.response?.data?.code === 'GUEST_MULTIPLAYER_FORBIDDEN') {
                setGuestGateOpen(true);
                return;
            }
            const msg = err?.response?.data?.error || 'Failed to accept invitation';
            toast.error(msg);
        }
    };

    return (
        <div className="page-login">
            <div className="wrapper">
                <h1>You're invited!</h1>
                <div className="form-block">
                    <div className="description">
                        <strong>{inviterName}</strong> invited you to join <strong>{leagueName}</strong> on Nova.
                    </div>
                    {isGuest || guestGateOpen ? (
                        <SignupGateModal
                            inline
                            open
                            redirectTo={redirectTarget}
                            title={`Save your team to join ${leagueName}`}
                            description="Create a full account to join this friends league without losing your team."
                        />
                    ) : (
                        <button
                            type="button"
                            className="submit-btn"
                            onClick={handleAccept}
                            disabled={acceptInvite.isPending}
                            style={{ marginTop: '1rem' }}
                        >
                            {acceptInvite.isPending ? 'JOINING…' : 'ACCEPT INVITATION'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export { LeagueInvitePage };
